import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Users, Heart, Play, Smartphone, Apple } from 'lucide-react';
import { HomeShortsStrip } from '../components/HomeShortsStrip';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const Home = () => {
  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Lion Image */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute right-0 top-0 w-full lg:w-2/3 h-full"
            style={{
              backgroundImage: `url(https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/n6h10t5c_Lion%20King%202.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
              maskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="max-w-2xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-gold uppercase tracking-[0.3em] text-sm mb-6"
            >
              Non-Denominational Ministry
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="font-cinzel text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              <span className="text-gradient-gold">TRUE</span>
              <br />
              <span className="text-foreground">BELIEVER</span>
              <br />
              <span className="text-deep-red">FAM</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground mb-8 max-w-lg"
            >
              A new church for a new time. Breaking traditional religious constraints by learning the true scripture meaning of the WORD.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-4"
            >
              <Link to="/join" className="btn-primary flex items-center gap-2" data-testid="hero-join-btn">
                Join The Fam
                <ArrowRight size={18} />
              </Link>
              <Link to="/services" className="btn-secondary flex items-center gap-2" data-testid="hero-services-btn">
                Watch Services
                <Play size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-cinzel text-3xl sm:text-4xl text-gold mb-6">Our Mission</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We aim to break traditional religious/denominational constraints by learning true scripture meaning of the WORD. Our focus is on biblical fundamentals, believing in the Godhead—YAHWEH, Yeshua (Jesus), and the Holy Spirit—while teaching biblical passages within their original context.
            </p>
          </motion.div>
        </div>
      </section>

      <HomeShortsStrip />

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {/* Weekly Service Card */}
            <motion.div 
              variants={fadeInUp}
              className="card-dark group"
              data-testid="feature-service"
            >
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                <Calendar className="text-gold" size={28} />
              </div>
              <h3 className="font-cinzel text-xl text-foreground mb-3">Weekly Service</h3>
              <p className="text-muted-foreground mb-4">
                Join us every Wednesday at 7:00 PM CST for B.I.D.D's (Break It Down Discussions) via Zoom.
              </p>
              <Link to="/services" className="text-gold flex items-center gap-2 text-sm uppercase tracking-wider hover:gap-4 transition-all">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* Community Card */}
            <motion.div 
              variants={fadeInUp}
              className="card-dark group"
              data-testid="feature-community"
            >
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                <Users className="text-gold" size={28} />
              </div>
              <h3 className="font-cinzel text-xl text-foreground mb-3">Community Outreach</h3>
              <p className="text-muted-foreground mb-4">
                We provide food, clothing, youth mentorship, and adult education to transform our community.
              </p>
              <Link to="/outreach" className="text-gold flex items-center gap-2 text-sm uppercase tracking-wider hover:gap-4 transition-all">
                Get Involved <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* Give Card */}
            <motion.div 
              variants={fadeInUp}
              className="card-dark group"
              data-testid="feature-donate"
            >
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                <Heart className="text-gold" size={28} />
              </div>
              <h3 className="font-cinzel text-xl text-foreground mb-3">Support the Ministry</h3>
              <p className="text-muted-foreground mb-4">
                Your generous donations help us spread the Word and serve our community with love and compassion.
              </p>
              <Link to="/donate" className="text-gold flex items-center gap-2 text-sm uppercase tracking-wider hover:gap-4 transition-all">
                Give Today <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* App Highlight Banner */}
      <section className="py-16" data-testid="home-app-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="card-dark gold-glow overflow-hidden"
          >
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_1c2b9578-16c6-47ba-a745-b83594265996/artifacts/esfzzpyh_The%20Holy%20WORD%20Bible%20icon%202%20%28512%20x%20512%20px%29%20%281%29.png"
                  alt="The Holy WORD Bible app icon"
                  className="w-24 h-24 flex-shrink-0 rounded-2xl border border-gold/30 shadow-lg"
                />
                <div>
                  <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-3">
                    Our Official App
                  </div>
                  <h2 className="font-cinzel text-2xl sm:text-3xl text-foreground mb-2">
                    The Holy <span className="text-gold">WORD</span> Bible
                  </h2>
                  <p className="text-muted-foreground max-w-md">
                    Read and study the Bible anywhere with our Ministry's official app — on Google Play, the App Store, and the web.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 lg:flex-col lg:items-stretch">
                <a
                  href="https://play.google.com/store/apps/details?id=app.emergent.biblialaunche1b2617f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="home-app-google-play"
                >
                  <Smartphone size={18} />
                  Google Play
                </a>
                <a
                  href="https://apps.apple.com/us/app/the-holy-word-bible/id6745786815"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="home-app-app-store"
                >
                  <Apple size={18} />
                  App Store
                </a>
                <Link
                  to="/bible-app"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="home-app-learn-more"
                >
                  Learn More
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section with Lion */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/1x0fdbpc_Lion%20King%201.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-cinzel text-4xl sm:text-5xl text-foreground mb-6">
              Join Our <span className="text-gold">Family</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Become part of a community that believes in the power of true scripture and fellowship. Together, we grow stronger in faith.
            </p>
            <Link to="/join" className="btn-primary inline-flex items-center gap-2" data-testid="cta-join-btn">
              Become a Member
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events Preview */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="font-cinzel text-3xl text-gold">Service Schedule</h2>
            <Link to="/events" className="text-gold flex items-center gap-2 text-sm uppercase tracking-wider hover:gap-4 transition-all">
              View Calendar <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-dark gold-glow"
              data-testid="upcoming-sabbath"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-cinzel text-gold">SAT</div>
                    <div className="text-4xl font-bold text-foreground">9:45</div>
                    <div className="text-muted-foreground text-sm">AM CST</div>
                  </div>
                  <div>
                    <span className="inline-block bg-gold/20 text-gold text-xs px-2 py-1 rounded-full mb-2">
                      In-Person & YouTube
                    </span>
                    <h3 className="font-cinzel text-xl text-foreground mb-1">Sabbath Service</h3>
                    <p className="text-muted-foreground text-sm">In person or via YouTube live stream</p>
                  </div>
                </div>
                <a 
                  href="https://www.youtube.com/channel/UCdJtc_ATtcQV6BKByvucFQA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary whitespace-nowrap text-sm py-2 px-4"
                >
                  Watch on YouTube
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-dark"
              data-testid="upcoming-bidds"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-cinzel text-gold">WED</div>
                    <div className="text-4xl font-bold text-foreground">7:00</div>
                    <div className="text-muted-foreground text-sm">PM CST</div>
                  </div>
                  <div>
                    <span className="inline-block bg-deep-red/20 text-deep-red text-xs px-2 py-1 rounded-full mb-2">
                      Virtual (Zoom)
                    </span>
                    <h3 className="font-cinzel text-xl text-foreground mb-1">Prayer & B.I.D.D.</h3>
                    <p className="text-muted-foreground text-sm">Virtual study of the Lost Books on Zoom</p>
                  </div>
                </div>
                <a 
                  href="https://us06web.zoom.us/j/94303159703?pwd=d1ZoRFRaK0w4V29qOVY2Mjc3dVVqQT09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary whitespace-nowrap text-sm py-2 px-4"
                >
                  Join on Zoom
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
